import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Agent } from '../agent'
import type * as common from '../common'
import type * as dms from '../dms'

import type { Access } from './access'
import type { Context } from './context'
import type * as page from './page'
import type { Scenario } from './scenario'
import type { Tenant } from './tenant'
import type { User } from './user'
import type { Zone } from './zone'

// NOTE: this makes creating a TypeChecker easier
type AppBase = common.Identified<App.Id> &
  common.Named &
  common.Aliased &
  common.Described.Optionally &
  common.CreatedBy<User.Id>
/**
 * Represents an application for an end user. The main purpose of Attain Apps is
 * to provide users with these applications
 *
 * Each {@link Tenant} and even {@link Zone} can have their own applications
 */
export interface App extends AppBase {
  /**
   * Icon that will be displayed in UI
   *
   * Must correspond to a supported icon in our frontend
   */
  icon: string
  /**
   * Indicates whether or not this {@link App} can have {@link Scenario}
   */
  supportsScenarios: boolean
  /**
   * Indicates whether user assignments for this {@link App} can grant
   * {@link Access.Flag.CanConfigureFunctionality}.
   */
  supportsFunctionalityConfiguration?: boolean
  /**
   * Indicates what variables the user can input when creating or updating the
   * scenario
   */
  scenarioVariables: readonly dms.data.query.Variable[]
  /**
   * Indicates what variables the user can set for their current session.
   */
  sessionVariables: readonly dms.data.query.Variable[]
}
export namespace App {
  /**
   * Identifies an {@link App}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('App.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * {@link App} that also has the full {@link Context.ForApp}
   */
  export type InContext = App & Context.ForApp

  /**
   * Information needed to create a {@link App}
   */
  export type Input = Omit<App, 'createdBy' | 'id'>

  /**
   * Information used to update a {@link App}
   */
  export type Updates = Partial<Input>

  /**
   * {@link App} with additional information pertaining to the associated
   * {@link User}
   */
  export interface ForUser extends App, Access.Holder {
    /**
     * A list of {@link App.Page} that are available to this user in this
     * {@link App}
     */
    pages: readonly App.Page[]
    /**
     * A list of {@link Scenario.ForUser} that this user has access to in this
     * {@link App.ForUser}
     */
    scenarios: readonly Scenario.ForUser[]
    /**
     * A list of {@link Agent.ForUser} that this user has access to in this
     * {@link App}
     */
    agents: readonly Agent.ForUser[]
  }

  /**
   * Represents the presentation of a {@link page.Page} within an {@link App}
   */
  export interface Page
    extends common.Identified<Page.Id>,
      common.Named,
      common.Aliased,
      common.Described.Optionally {
    /**
     * Reference to the actual {@link page.Page}
     */
    pageId: page.Page.Id
    /**
     * Determines how this {@link App.Page} is surfaced to a user
     *
     * `undefined` indicates this {@link App.Page} is not displayed by default.
     * In other words, a user can only visit this {@link App.Page} by navigating
     * to it from another {@link App.Page}
     *
     * @see {@link App.Page.DisplayInfo}, {@link navigationId}
     */
    display?: Page.DisplayInfo
    variables?: dms.data.query.Variable.Lookup
  }
  export namespace Page {
    /**
     * Identifies an {@link App.Page}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = string
    export namespace Id {
      export const isId: TypeChecker<Id> =
        TypeChecker.isString.usingAlias('App.Page.Id')
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Input = Omit<Page, 'id' | 'pageId'> & {
      /**
       * In some cases, we don't know the ID, we only know the alias of the page
       * we want to use. In that case, under the hood, the alias will be used to
       * retrieve the ID (at the moment the {@link Input} is used)
       */
      page: common.FlexibleIdentifier<page.Page.Id>
    }

    // TODO: make this partial

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Updates = Input

    /**
     * Determines how an {@link App.Page} is surfaced to a user
     */
    export interface DisplayInfo extends common.Positioned {
      /**
       * Determines if this {@link App.Page} should be presented prominently, as
       * a main page (`true`), or behind a menu or overflow, as an extra utility
       * page (`false`)
       */
      isMain: boolean
    }

    export namespace DisplayInfo {
      /**  @see {@link App.Page.DisplayInfo} */
      export function getDefault(): DisplayInfo {
        return { isMain: true, position: 1 }
      }
    }
  }
}
